/**
 * Analyzes message content and extracts key insights
 */
interface ContentAnalysis {
  shouldShowInsights: boolean
  insights: string[]
}
/**
 * Determines if a message should have an insight block
 * and extracts key points if so
 */
export function analyzeMessageForInsights(content: string): ContentAnalysis {
  // Skip if content is too short
  if (content.length < 200) {
    return { shouldShowInsights: false, insights: [] }
  }
  const insights: string[] = []
  // Extract bullet points
  const bulletPoints = extractBulletPoints(content)
  // Extract key sentences from paragraphs
  const keySentences = extractKeySentences(content)
  // Extract table summaries
  const tableSummaries = extractTableSummaries(content)
  // Combine and prioritize insights
  insights.push(...bulletPoints.slice(0, 2))
  insights.push(...keySentences.slice(0, 2))
  insights.push(...tableSummaries.slice(0, 1))
  // Filter and clean
  const cleanedInsights = insights
    .filter(Boolean)
    .filter((insight) => insight.length > 10 && insight.length < 150)
    .slice(0, 4)
    .map((insight) => cleanInsight(insight))
  // Determine if we should show insights
  const shouldShow =
    cleanedInsights.length >= 2 &&
    (bulletPoints.length > 4 ||
      content.split('\n\n').length > 3 ||
      content.includes('|') || // Has tables
      content.split('\n').filter((line) => line.trim().startsWith('#')).length >
        2) // Multiple headings
  return {
    shouldShowInsights: shouldShow,
    insights: cleanedInsights,
  }
}
/**
 * Extract bullet points from content
 */
function extractBulletPoints(content: string): string[] {
  const lines = content.split('\n')
  const bullets: string[] = []
  for (const line of lines) {
    // Match various bullet formats
    const match = line.match(/^[\s]*[-*•]\s+(.+)$/)
    if (match) {
      bullets.push(match[1].trim())
    }
  }
  return bullets
}
/**
 * Extract key sentences from paragraphs
 */
function extractKeySentences(content: string): string[] {
  const sentences: string[] = []
  // Split into paragraphs
  const paragraphs = content
    .split('\n\n')
    .filter((p) => p.trim().length > 0)
    .filter((p) => !p.includes('|')) // Skip tables
    .filter((p) => !p.trim().startsWith('#')) // Skip headings
    .filter((p) => !p.trim().startsWith('-')) // Skip lists
  for (const paragraph of paragraphs) {
    // Get first sentence
    const firstSentence = paragraph
      .split(/[.!?]/)
      .filter((s) => s.trim().length > 20)[0]
    if (firstSentence) {
      sentences.push(firstSentence.trim())
    }
  }
  return sentences
}
/**
 * Extract summaries from tables
 */
function extractTableSummaries(content: string): string[] {
  const summaries: string[] = []
  const lines = content.split('\n')
  let inTable = false
  let tableHeaders: string[] = []
  for (const line of lines) {
    if (line.includes('|') && line.split('|').length > 2) {
      if (!inTable) {
        // First row - headers
        tableHeaders = line
          .split('|')
          .map((h) => h.trim())
          .filter(Boolean)
        inTable = true
      }
    } else if (inTable) {
      // End of table
      if (tableHeaders.length > 0) {
        summaries.push(`Table shows ${tableHeaders.join(', ')}`)
      }
      inTable = false
      tableHeaders = []
    }
  }
  return summaries
}
/**
 * Clean and format an insight
 */
function cleanInsight(insight: string): string {
  // Remove markdown formatting
  let cleaned = insight
    .replace(/\*\*/g, '') // Bold
    .replace(/\*/g, '') // Italic
    .replace(/`/g, '') // Code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links
    .trim()
  // Ensure it ends with period
  if (!cleaned.match(/[.!?]$/)) {
    cleaned += '.'
  }
  // Capitalize first letter
  cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
  // Truncate if too long
  if (cleaned.length > 140) {
    cleaned = cleaned.substring(0, 137) + '...'
  }
  return cleaned
}
/**
 * Check if content has structured elements
 */
export function hasStructuredContent(content: string): boolean {
  const bulletCount = (content.match(/^[\s]*[-*•]\s+/gm) || []).length
  const paragraphCount = content.split('\n\n').length
  const hasTable = content.includes('|')
  const headingCount = (content.match(/^#+\s+/gm) || []).length
  return bulletCount > 4 || paragraphCount > 3 || hasTable || headingCount > 2
}
